/* ═══ Prospect → draft line items ═══
   Deterministic mapper from a website quote submission's structured answers to
   RATE_CATALOG line items. Pure: no AI call, no persistence — the draft loads
   into the builder for the operator to edit before anything saves.

   Approved rules (Ivin 2026-08-06):
   - Range counts draft at the LOW end ("2-5" → qty 2) — never overquote.
   - Object cm sizes map to the catalog tier covering their upper half:
     under 10cm → 3-8 in, 10-30cm → 8-14 in, 30cm-2m → 2-4 ft, 2m+ → flag only.
   - Full body defaults to the Big Rig path (body_scan + studio session).
   - Bust drafts as a Head Scan line.
   - LiDAR is NEVER auto-priced (estimator only) — measurement-grade/both
     environments and vehicle-scale objects become flags, not lines.
   Anything the mapper can't price becomes a flag line the convert flow appends
   to the internal notes, so nothing silently disappears. */

import { RATE_CATALOG } from "./constants.js";
import { getTierRate } from "./utils.js";

const ALL_CATALOG = [
  ...RATE_CATALOG.sessions, ...RATE_CATALOG.scanning, ...RATE_CATALOG.leo,
  ...RATE_CATALOG.lowpoly, ...RATE_CATALOG.objects, ...RATE_CATALOG.environments,
  ...RATE_CATALOG.addons,
];

// ─── Effective answer resolution ───
// Mirror of the website server's effectiveAnswer(): customer-typed wins over
// enum-inferred, which wins over text-span-inferred (locked principle 4).
const inferredValue = (raw) => (raw && typeof raw === "object" && "value" in raw ? raw.value : raw);
export function effectiveAnswer(raw, qid) {
  const a = raw?.answers ? raw.answers[qid] : undefined;
  if (a !== undefined && a !== null && a !== "") return a;
  const e = raw?.enumInferredAnswers ? raw.enumInferredAnswers[qid] : undefined;
  if (e !== undefined && e !== null) return inferredValue(e);
  const s = raw?.textSpanInferredAnswers ? raw.textSpanInferredAnswers[qid] : undefined;
  if (s && s.value !== undefined && s.value !== null) return s.value;
  return null;
}
const asList = (v) => (Array.isArray(v) ? v : v == null || v === "" ? [] : [v]);

// Questionnaire count answers are ranges; draft at the low end (approved).
const P_COUNT_LOW = { "1": 1, "2_5": 2, "6_12": 6, "13_plus": 13 };
const O_COUNT_LOW = { "1": 1, "2_5": 2, "6_20": 6, "20_plus": 20 };

// Object size (cm ranges) → catalog tier key. null = no catalog rate (2m+ routes
// to the LiDAR estimator like any large-scale capture).
const O_SIZE_TIER = {
  under_10cm: "utensil",   // 3-8 in
  "10_30cm": "shoe",       // 8-14 in
  "30cm_2m": "suitcase",   // 2-4 ft
  "2m_5m": null,
  over_5m: null,
};

// Surface answers → catalog difficulty suffix. Mirror-chrome, clear parts, or
// confirmed transparency price as complex; glossy/dark/soft surfaces as detailed.
// "not_sure" answers stay standard — uncertainty is surfaced via notes, not price.
export function objectDifficulty({ materials, finishes, color, transparent }) {
  if (finishes.includes("chrome_mirror") || materials.includes("clear_parts") || transparent === "yes") return "cpx";
  if (finishes.includes("glossy") || color === "dark" || materials.includes("fabric") || materials.includes("organic")) return "mod";
  return "std";
}

// Travel band by ZIP prefix. Best-effort split of the catalog's zone notes
// (local: Brooklyn/Manhattan/W Queens/Bronx · mid: SI/E Queens/close NJ/lower
// Westchester); the exact prefix list is an open question with Ivin, so anything
// unmatched flags for manual review instead of guessing.
const LOCAL_PREFIXES = new Set(["100", "101", "102", "104", "111", "112"]);
const MID_PREFIXES = new Set(["103", "105", "106", "107", "108", "113", "114", "116",
  "070", "071", "072", "073", "074", "075", "076"]);

// PortalCam gaussian sqft tiers (mirrors the env_portalcam catalog note).
function portalcamRate(sqft) {
  const per = sqft < 2000 ? 0.25 : sqft <= 10000 ? 0.18 : 0.15;
  return { total: Math.max(200, Math.round(sqft * per)), per };
}

// e_sqft is free text ("3,500 sq ft", "6 rooms", "2 acres"). Only a plausible
// square-foot figure prices; acreage and room counts flag for manual sizing.
export function parseSqft(text) {
  const s = String(text || "");
  if (!s.trim() || /acre/i.test(s)) return null;
  const m = s.replace(/,/g, "").match(/(\d+(?:\.\d+)?)/);
  if (!m) return null;
  const n = Number(m[1]);
  return n >= 100 ? n : null;
}

let liSeq = 0;
function catalogLine(id, qty, noteOverride) {
  const cat = ALL_CATALOG.find((c) => c.id === id);
  if (!cat) return null;
  const rate = cat.tiered ? getTierRate(id, qty) : cat.rate;
  return {
    key: `li_prospect_${liSeq++}_${id}`,
    catalogId: id,
    description: cat.label,
    qty,
    rate,
    notes: noteOverride !== undefined ? noteOverride : (cat.notes || ""),
    isCustomRate: false,
    discount: null,
  };
}

/* Returns { items, flags }. Flags are operator-facing sentences describing what
   the draft could not (or must not) price — the convert flow appends them to the
   quote's internal notes. */
export function prospectDraftLineItems(raw) {
  const items = [];
  const flags = [];
  const types = new Set(Array.isArray(raw?.captureTypes) ? raw.captureTypes : []);
  const location = raw?.location || effectiveAnswer(raw, "q_location");
  const isStudio = location === "our_brooklyn_studio";
  const isOnSite = location === "on_site";

  // ── People ──
  if (types.has("people")) {
    const countKey = effectiveAnswer(raw, "p_count");
    const count = P_COUNT_LOW[countKey] || 1;
    if (!P_COUNT_LOW[countKey]) flags.push("People count unanswered — scan lines drafted at qty 1.");
    else if (countKey !== "1") flags.push(`People count is a range (${countKey.replace("_", "-").replace("plus", "+")}); drafted at the low end (${count}).`);
    const coverage = asList(effectiveAnswer(raw, "p_coverage"));
    const scanLines = [];
    if (coverage.includes("full_body")) scanLines.push(catalogLine("body_scan", count));
    if (coverage.includes("head") || coverage.includes("bust")) scanLines.push(catalogLine("head_scan", count));
    if (coverage.includes("hands")) scanLines.push(catalogLine("leo_hands", count));
    if (coverage.includes("other")) {
      const detail = effectiveAnswer(raw, "p_coverage_other");
      flags.push(`Coverage "other"${detail ? ` (${detail})` : ""} not priced — add a Leo line manually.`);
    }
    if (!coverage.length) flags.push("People coverage unanswered — no scan lines drafted.");
    const hasArray = scanLines.some((li) => li && (li.catalogId === "body_scan" || li.catalogId === "head_scan"));
    // Session fees apply to camera-array (Big Rig / Head Rig) captures only.
    if (hasArray && isStudio) items.push(catalogLine(count <= 2 ? "half_art" : "full_art", 1));
    if (hasArray && isOnSite) flags.push("On-site camera-array capture — add the on-location session line (full_onlocation) manually; it changes how per-capture charges apply.");
    items.push(...scanLines);
  }

  // ── Pets ──
  if (types.has("pets")) {
    flags.push("Pets capture requested — no catalog rates exist; price manually.");
  }

  // ── Objects ──
  if (types.has("objects")) {
    const countKey = effectiveAnswer(raw, "o_count");
    const count = O_COUNT_LOW[countKey] || 1;
    if (!O_COUNT_LOW[countKey]) flags.push("Object count unanswered — drafted at qty 1.");
    else if (countKey !== "1") flags.push(`Object count is a range (${countKey.replace("_", "-").replace("plus", "+")}); drafted at the low end (${count}).`);
    const sizeKey = effectiveAnswer(raw, "o_size");
    const tier = O_SIZE_TIER[sizeKey];
    if (tier) {
      const diff = objectDifficulty({
        materials: asList(effectiveAnswer(raw, "o_material")),
        finishes: asList(effectiveAnswer(raw, "o_finish")),
        color: effectiveAnswer(raw, "o_color"),
        transparent: effectiveAnswer(raw, "o_transparent"),
      });
      items.push(catalogLine(`obj_${tier}_${diff}`, count));
      if (count > 10) flags.push("Object count over 10 — check the additional-captures rate (extra_captures).");
    } else if (sizeKey === "2m_5m" || sizeKey === "over_5m") {
      flags.push("Object over 2 m — no catalog rate; price via the LiDAR estimator.");
    } else {
      flags.push("Object size unanswered — no object line drafted.");
    }
    if (isOnSite) flags.push("On-site object work — add an on-location session band (env_nimble_onloc / env_onloc_full) unless this is a single-object local drop-in.");
  }

  // ── Environments ──
  if (types.has("environments")) {
    const purpose = effectiveAnswer(raw, "e_purpose");
    if (purpose === "viewing_only") {
      const sqft = parseSqft(effectiveAnswer(raw, "e_sqft"));
      if (sqft) {
        const { total, per } = portalcamRate(sqft);
        const line = catalogLine("env_portalcam", 1, `${sqft.toLocaleString()} sq ft × $${per}/sq ft`);
        line.rate = total;
        items.push(line);
      } else {
        items.push(catalogLine("env_portalcam", 1));
        flags.push(`Gaussian capture has no usable square footage${effectiveAnswer(raw, "e_sqft") ? ` (client wrote "${effectiveAnswer(raw, "e_sqft")}")` : ""} — PortalCam line drafted at $0.`);
      }
    } else {
      // measurement_grade / both / not_sure → LiDAR territory, estimator only.
      flags.push("Environment needs geometry (or purpose unclear) — LiDAR is priced only via the LiDAR estimator; run it to add the line.");
    }
  }

  // ── Travel ──
  if (isOnSite) {
    const zip = String(raw?.zipCode || effectiveAnswer(raw, "q_zip") || "").trim();
    const prefix = zip.slice(0, 3);
    if (LOCAL_PREFIXES.has(prefix)) items.push(catalogLine("travel_local", 1));
    else if (MID_PREFIXES.has(prefix)) items.push(catalogLine("travel_mid", 1));
    else flags.push(`On-site ZIP ${zip || "(missing)"} is outside the local/mid travel bands — travel needs a manual quote.`);
  } else if (!isStudio && types.size > 0) {
    flags.push("Location undecided — no session or travel lines drafted.");
  }

  return { items: items.filter(Boolean), flags };
}
